/**
 * Helpers for resolving JSON-path enum state from a MetaInfo `enumColumns`
 * list. Used by the Peek popover and the JSON structure details dialog to
 * decide which inline glyph to render next to a property name.
 */
import type { EnumColumnDeclaration } from '@/types/electron';
import type { JsonSubOperator } from '../../core/types/workspaceTypes';
import { hasJsonPathWildcard } from './jsonFilterSql';
import { findWithSchemaFold } from './metaInfoSchemaMatch';

/**
 * Returns the declaration for `(schema, table, column, path)` if one exists.
 * A path-scoped declaration is identified by all four fields together; the
 * column-level declaration (no `path`) deliberately does NOT match a path.
 */
export function findEnumDeclarationForPath(
  enumColumns: ReadonlyArray<EnumColumnDeclaration> | undefined,
  schema: string,
  table: string,
  column: string,
  path: string,
): EnumColumnDeclaration | undefined {
  if (!enumColumns || enumColumns.length === 0) return undefined;
  return findWithSchemaFold(
    enumColumns,
    schema,
    (d) => d.table === table && d.column === column && d.path === path,
  );
}

/**
 * Default JSON sub-operator for a path that has a matching enum declaration.
 *
 * - Scalar declarations on simple paths → `'property_enum'` (single-row
 *   equality / IN against the extracted scalar).
 * - Array declarations OR any path containing `[*]` → `'any_enum'`. Wildcard
 *   paths can't use the scalar `extractTextSql(...)` shape — that emit
 *   returns NULL on PG and rejects on MSSQL/SQLite for `[*]`. `any_enum`
 *   routes through the wildcard-aware aggregate emit, which is correct
 *   regardless of whether the declaration is stored as `'scalar'` or
 *   `'array'` (a scalar-leaf reached through an array unnest, e.g.
 *   `$.ReportFields[*].DataType`, is declared `pathKind: 'scalar'` but
 *   semantically needs the any-element predicate).
 */
export function defaultSubOpForEnumDeclaration(
  declaration: EnumColumnDeclaration,
  path: string,
): JsonSubOperator {
  if (hasJsonPathWildcard(path) || declaration.pathKind === 'array') {
    return 'any_enum';
  }
  return 'property_enum';
}
